import { DestroyRef, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AssignmentSectionQuestionQuery,
  AssignmentSectionQuestionService,
  AssignmentSectionQuestionUIModel,
} from '@examdojo/assignment';
import {
  ChallengeActivityQuery,
  ChallengeActivityQuestionAttemptQuery,
  ChallengeActivityQuestionAttemptService,
  ChallengeActivityQuestionAttemptStoreModel,
  ChallengeActivityService,
  ChallengeActivityStore,
  ChallengeActivityUIModel,
} from '@examdojo/challenge';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { mapToVoid, shareOneReplay } from '@examdojo/core/rxjs';
import {
  calculateQuestionTotalMarks,
  getGradingResultAwardedMarksFromQuestionItems,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptEntityUIModel,
  QuestionGradingResultData,
  QuestionsWithAttemptStatuses,
  QuestionSelectorDataSource,
  QuestionSelectorItem,
  toMixedFraction,
} from '@examdojo/question';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  EMPTY,
  filter,
  finalize,
  map,
  Observable,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { MultiAttemptSelfGradingSelectionService } from '../effects/multi-attempt-self-grading-selection.service';

@Injectable()
export class ChallengeActivityQuestionSelectorService implements QuestionSelectorDataSource {
  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly challengeActivityQuestionAttemptQuery: ChallengeActivityQuestionAttemptQuery,
    private readonly assignmentSectionQuestionService: AssignmentSectionQuestionService,
    private readonly assignmentSectionQuestionQuery: AssignmentSectionQuestionQuery,
    private readonly questionAttemptEntityService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly challengeActivityQuestionAttemptService: ChallengeActivityQuestionAttemptService,
    private readonly challengeActivityService: ChallengeActivityService,
    private readonly multiAttemptSelfGradingSelectionService: MultiAttemptSelfGradingSelectionService,
    private readonly challengeActivityStore: ChallengeActivityStore,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {
    this.setupNavigationDebounce().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly isTransitioningBetweenQuestions = signal(false);

  private readonly navigationDebounce$ = new Subject<{
    question: AssignmentSectionQuestionUIModel;
    challengeActivityId: string;
  }>();

  readonly questionsWithAttemptStatuses$: Observable<QuestionsWithAttemptStatuses[]> =
    this.assignmentSectionQuestionQuery.entities$.pipe(
      switchMap((questions) =>
        this.challengeActivityQuestionAttemptQuery.entities$.pipe(
          switchMap((attempts) =>
            combineLatest([
              this.questionAttemptQuery.entities$,
              this.multiAttemptSelfGradingSelectionService.selfGradingResultsPerAttempt$,
            ]).pipe(
              map(([questionAttemptEntities, selfGradingResults]) => {
                const relevantEntities = questionAttemptEntities.filter((entity) =>
                  attempts.some((attempt) => attempt.question_id === entity.attempt.question_id),
                );
                return this.buildQuestionsWithAttemptStatuses(
                  questions,
                  attempts,
                  relevantEntities,
                  selfGradingResults,
                );
              }),
            ),
          ),
        ),
      ),
      shareOneReplay(),
    );

  readonly allQuestionAttemptsDone$ = this.questionsWithAttemptStatuses$.pipe(
    map((questions) => questions.every((q) => q.attemptStatus === 'DONE')),
    distinctUntilChanged(),
    shareOneReplay(),
  );

  readonly nextUnfinishedQuestion$: Observable<AssignmentSectionQuestionUIModel | null> = combineLatest([
    this.questionsWithAttemptStatuses$,
    this.assignmentSectionQuestionQuery.active$,
  ]).pipe(
    map(([questions, activeQuestion]) => this.findNextUnfinishedQuestion(questions, activeQuestion)),
    shareOneReplay(),
  );

  selectQuestion(question: QuestionSelectorItem): void {
    const activeChallengeActivityId = this.challengeActivityQuery.getActiveId();
    if (!activeChallengeActivityId) {
      return;
    }
    this.setChallengeQuestion(question.assignmentSectionQuestion, activeChallengeActivityId);
  }

  setChallengeQuestion(
    question: AssignmentSectionQuestionUIModel,
    challengeActivityId: ChallengeActivityUIModel['id'],
  ): void {
    if (this.assignmentSectionQuestionService.isQuestionActive(question.assignment_id, question.question_id)) {
      return;
    }

    this.assignmentSectionQuestionService.setActiveQuestion(question.assignment_section_id, question.question_id);
    this.activateExistingQuestionAttempt(challengeActivityId, question.question_id);
    this.challengeActivityStore.setNavigating(true);
    this.navigationDebounce$.next({ question, challengeActivityId });
  }

  syncQuestionAttemptWithOngoingChallengeActivity() {
    return combineLatest([
      this.challengeActivityQuery.activeEntity$.pipe(filter(Boolean), distinctUntilKeyChanged('current_question_id')),
      this.assignmentSectionQuestionQuery.entities$.pipe(filter((questions) => questions.length > 0)),
    ]).pipe(
      filter(([activity]) => !this.isAlreadyOnQuestion(activity.current_question_id)),
      switchMap(([activity]) => {
        this.isTransitioningBetweenQuestions.set(true);
        return this.selectOrCreateQuestionAttempt(activity).pipe(
          finalize(() => this.isTransitioningBetweenQuestions.set(false)),
        );
      }),
    );
  }

  private activateExistingQuestionAttempt(challengeActivityId: string, questionId: number): void {
    const attempt = this.challengeActivityQuestionAttemptQuery.getByQuestionId(challengeActivityId, questionId);

    this.setActiveAttempts(challengeActivityId, questionId, attempt?.question_attempt_id)
      .pipe(
        catchError((err: unknown) => {
          this.errorHandlerService.error('Failed to activate question attempt', {
            toast: 'Could not load question. Please try again.',
            err,
          });
          return EMPTY;
        }),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private selectOrCreateQuestionAttempt(activity: ChallengeActivityUIModel): Observable<void> {
    const { id: activityId, current_question_id: questionId, assignment_section_id } = activity;

    if (!activityId || !questionId) {
      return EMPTY;
    }

    this.assignmentSectionQuestionService.setActiveQuestion(assignment_section_id, questionId);

    const existingAttempt = this.challengeActivityQuestionAttemptQuery.getByQuestionId(activityId, questionId);

    return this.setActiveAttempts(activityId, questionId, existingAttempt?.question_attempt_id).pipe(
      catchError((err: unknown) => {
        this.errorHandlerService.error('Failed to select question attempt', {
          toast: 'Could not load question. Please try again.',
          err,
        });
        return EMPTY;
      }),
    );
  }

  private setActiveAttempts(
    challengeActivityId: string,
    questionId: number,
    questionAttemptId: string | undefined,
  ): Observable<void> {
    this.challengeActivityQuestionAttemptService.setActive(challengeActivityId, questionId);

    if (!questionAttemptId) {
      return EMPTY;
    }

    if (this.questionAttemptQuery.hasEntity(questionAttemptId)) {
      this.questionAttemptEntityService.setActive(questionAttemptId);
      return EMPTY;
    }

    return this.questionAttemptEntityService
      .fetchQuestionAttemptDetails(questionAttemptId, { setActive: true })
      .pipe(mapToVoid());
  }

  private setupNavigationDebounce() {
    return this.navigationDebounce$.pipe(
      debounceTime(300),
      tap(() => this.challengeActivityStore.setNavigating(false)),
      switchMap(({ question, challengeActivityId }) =>
        this.challengeActivityService.setCurrentQuestion(challengeActivityId, question.question_id).pipe(
          catchError((err: unknown) => {
            this.errorHandlerService.error('Failed to switch challenge question', {
              toast: 'Could not load question. Please try again.',
              err,
            });
            return EMPTY;
          }),
        ),
      ),
    );
  }

  private isAlreadyOnQuestion(questionId: number | null | undefined): boolean {
    if (!questionId) {
      return false;
    }
    return this.questionAttemptQuery.getActive()?.attempt.question_id === questionId;
  }

  private findNextUnfinishedQuestion(
    questions: QuestionsWithAttemptStatuses[],
    activeQuestion: AssignmentSectionQuestionUIModel | null,
  ): AssignmentSectionQuestionUIModel | null {
    if (!activeQuestion) {
      return null;
    }

    const activeIndex = questions.findIndex(
      (q) => q.assignmentSectionQuestion.question_id === activeQuestion.question_id,
    );

    const afterActive = questions.slice(activeIndex + 1).find((q) => q.attemptStatus !== 'DONE');
    if (afterActive) {
      return afterActive.assignmentSectionQuestion;
    }

    const beforeActive = questions.slice(0, activeIndex).find((q) => q.attemptStatus !== 'DONE');
    return beforeActive?.assignmentSectionQuestion ?? null;
  }

  private buildQuestionsWithAttemptStatuses(
    questions: AssignmentSectionQuestionUIModel[],
    attempts: ChallengeActivityQuestionAttemptStoreModel[],
    questionAttemptEntities: QuestionAttemptEntityUIModel[],
    selfGradingResults: Map<string, { awardedMarks: number }>,
  ): QuestionsWithAttemptStatuses[] {
    return questions.map((question) => {
      const entity = questionAttemptEntities.find((e) => e.attempt.question_id === question.question_id);
      const attempt = attempts.find((a) => a.question_id === question.question_id);

      return {
        assignmentSectionQuestion: question,
        attemptStatus: attempt?.status ?? 'TODO',
        questionAttemptStatus: entity?.attempt.status ?? null,
        questionGradingResult: this.getGradingResult(entity, selfGradingResults),
      };
    });
  }

  private getGradingResult(
    entity: QuestionAttemptEntityUIModel | undefined,
    selfGradingResults: Map<string, { awardedMarks: number }>,
  ): QuestionGradingResultData | null {
    if (!entity) {
      return null;
    }

    const { attempt, questionItems, markCalculationMethod } = entity;

    const totalMarksFraction = toMixedFraction(entity.question?.max_points_fraction ?? null);
    const totalMarks = entity.question?.max_points ?? calculateQuestionTotalMarks(questionItems, markCalculationMethod);

    if (attempt.status === 'GRADED' && attempt.grading_result) {
      return {
        awardedMarks: getGradingResultAwardedMarksFromQuestionItems(questionItems),
        totalMarks,
        totalMarksFraction,
      };
    }

    if (attempt.status === 'FAILED') {
      const selfGrading = selfGradingResults.get(entity.id);
      if (selfGrading) {
        return {
          awardedMarks: selfGrading.awardedMarks,
          totalMarks,
          totalMarksFraction,
          isSelfGrading: true,
        };
      }
    }

    return null;
  }
}
